import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as XLSX from 'xlsx';
import {
  getSDEEvent,
  getEventRegistrations,
  getEventMeetings,
  getAllCandidatures,
  upsertMeeting,
  deleteMeeting,
  upsertCandidature,
  updateCandidatureStatus,
} from '../../Components/SDE24/sdeSupabaseService';

// ─── Theme ─────────────────────────────────────────
const theme = {
  primary: '#2563EB',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  gray: { 50: '#F9FAFB', 100: '#F3F4F6', 200: '#E5E7EB', 300: '#D1D5DB', 400: '#9CA3AF', 500: '#6B7280', 600: '#4B5563', 700: '#374151', 800: '#1F2937' },
};

// ─── Styled ────────────────────────────────────────
const Page = styled.div` max-width: 100%; margin: 0 auto; padding: 2rem; overflow-x: auto; `;

const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.5rem; flex-wrap: wrap; gap: 1rem;
  h1 { font-size: 1.5rem; font-weight: 700; color: ${theme.gray[800]}; margin: 0; }
`;

const BackBtn = styled.button`
  background: white; border: 1px solid ${theme.gray[300]}; padding: 0.5rem 1rem; border-radius: 8px; cursor: pointer; font-size: 0.875rem;
  &:hover { background: ${theme.gray[50]}; }
`;

const ExportBtn = styled.button`
  padding: 0.5rem 1rem; border-radius: 8px; font-size: 0.85rem; font-weight: 600; cursor: pointer; border: 1px solid ${theme.primary}; color: ${theme.primary}; background: white;
  &:hover { background: ${theme.primary}10; }
`;

const TableTag = styled.span`
  display: block; font-size: 0.7rem; font-weight: 500; color: ${theme.gray[500]}; margin-top: 2px;
`;

const StatsRow = styled.div`
  display: flex; gap: 1rem; margin-bottom: 1.5rem; flex-wrap: wrap;
`;

const StatPill = styled.div`
  padding: 0.4rem 1rem; background: white; border: 1px solid ${theme.gray[200]}; border-radius: 8px;
  font-size: 0.85rem; color: ${theme.gray[600]};
  span { font-weight: 700; color: ${theme.gray[800]}; margin-right: 0.25rem; }
`;

const GridWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid ${theme.gray[200]};
  border-radius: 12px;
  background: white;
`;

const Grid = styled.table`
  border-collapse: collapse; width: 100%; min-width: 800px;
  th, td { border: 1px solid ${theme.gray[200]}; padding: 0; text-align: center; }
`;

const SlotHeader = styled.th`
  padding: 0.6rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${theme.gray[50]};
  color: ${theme.gray[600]};
  min-width: 100px;
  white-space: nowrap;
  position: sticky; top: 0; z-index: 2;
`;

const CompanyHeader = styled.th`
  padding: 0.6rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${theme.gray[700]};
  background: white;
  text-align: left;
  min-width: 160px;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  position: sticky; left: 0; z-index: 1; border-right: 2px solid ${theme.gray[300]};
`;

const Cell = styled.td`
  min-width: 100px; height: 48px; cursor: pointer; position: relative;
  transition: background-color 0.15s;
  background: ${p =>
    p.hasConflict ? '#FEF2F2' :
    p.hasMeeting ? '#EFF6FF' :
    p.isCandidate ? '#F0FDF4' :
    'white'};
  &:hover { background: ${p => p.hasMeeting ? '#DBEAFE' : '#F3F4F6'}; }
`;

const MeetingPill = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${theme.primary};
  padding: 2px 4px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ConflictIcon = styled.span`
  color: ${theme.error}; font-size: 0.7rem; position: absolute; top: 2px; right: 2px;
`;

const CandidateDot = styled.div`
  width: 6px; height: 6px; border-radius: 50%; background: ${theme.success};
  position: absolute; bottom: 4px; right: 4px;
`;

const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 100;
`;

const Modal = styled.div`
  background: white; border-radius: 12px; padding: 1.5rem; max-width: 420px; width: 90%;
  box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  h3 { margin: 0 0 1rem; font-size: 1.1rem; font-weight: 600; color: ${theme.gray[800]}; }
`;

const ModalSelect = styled.select`
  width: 100%; padding: 0.6rem; border: 1px solid ${theme.gray[300]}; border-radius: 8px; font-size: 0.9rem; margin-bottom: 0.75rem;
  &:focus { outline: none; border-color: ${theme.primary}; }
`;

const ModalRow = styled.div`
  display: flex; gap: 0.5rem; justify-content: flex-end; margin-top: 1rem;
`;

const Btn = styled.button`
  padding: 0.5rem 1rem; border-radius: 8px; font-size: 0.85rem; font-weight: 600; cursor: pointer; border: none;
  background: ${p => p.variant === 'primary' ? theme.primary : p.variant === 'danger' ? theme.error : theme.gray[100]};
  color: ${p => (p.variant === 'primary' || p.variant === 'danger') ? 'white' : theme.gray[700]};
  &:hover { opacity: 0.9; }
`;

const LoadingBox = styled.div`
  display: flex; align-items: center; justify-content: center; min-height: 60vh; font-size: 1.1rem; color: ${theme.gray[500]};
`;

const LegendRow = styled.div`
  display: flex; gap: 1.5rem; margin-bottom: 1rem; font-size: 0.8rem; color: ${theme.gray[500]};
  align-items: center;
`;

const LegendItem = styled.div`
  display: flex; align-items: center; gap: 0.35rem;
`;

const LegendBox = styled.div`
  width: 14px; height: 14px; border-radius: 3px;
  background: ${p => p.color};
  border: 1px solid ${p => p.border || p.color};
`;

// ─── Component ─────────────────────────────────────
const SDEPlanningAdmin = ({ fullUser }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const eventId = searchParams.get('eid');

  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [allDesigners, setAllDesigners] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [candidatures, setCandidatures] = useState([]);
  const [modal, setModal] = useState(null); // { slotId, companyId }

  useEffect(() => {
    if (!eventId) return;
    loadData();
    // eslint-disable-next-line
  }, [eventId]);

  const loadData = async () => {
    setLoading(true);
    const [evRes, popARes, popBRes, meetRes, candRes] = await Promise.all([
      getSDEEvent(eventId),
      getEventRegistrations(eventId, 'pop_a'),
      getEventRegistrations(eventId, 'pop_b'),
      getEventMeetings(eventId),
      getAllCandidatures(eventId),
    ]);
    setEvent(evRes.data);
    const byName = (a, b) => (a.company_name || a.user_email || '').trim().localeCompare((b.company_name || b.user_email || '').trim(), 'fr', { sensitivity: 'base' });
    setCompanies((popARes.data || []).filter(r => r.status === 'submitted' || r.status === 'validated').sort(byName));
    setAllDesigners(popBRes.data || []);
    setDesigners((popBRes.data || []).filter(r => r.status === 'submitted' || r.status === 'validated').sort(byName));
    setMeetings(meetRes.data || []);
    setCandidatures(candRes.data || []);
    setLoading(false);
  };

  const slots = useMemo(() => {
    return event?.sde_config?.slots || [
      { id: 'slot1', label: 'Créneau 1' },
      { id: 'slot2', label: 'Créneau 2' },
      { id: 'slot3', label: 'Créneau 3' },
      { id: 'slot4', label: 'Créneau 4' },
      { id: 'slot5', label: 'Créneau 5' },
      { id: 'slot6', label: 'Créneau 6' },
      { id: 'slot7', label: 'Créneau 7' },
    ];
  }, [event]);

  // Build lookup: meeting at (slot, company)
  const meetingAt = useMemo(() => {
    const map = {};
    meetings.forEach(m => {
      map[`${m.slot_id}_${m.company_registration_id}`] = m;
    });
    return map;
  }, [meetings]);

  // Build lookup: is designer busy at slot?
  const designerBusy = useMemo(() => {
    const map = {};
    meetings.forEach(m => {
      map[`${m.slot_id}_${m.designer_registration_id}`] = true;
    });
    return map;
  }, [meetings]);

  // Build lookup: confirmed candidatures for company
  const confirmedCandidatures = useMemo(() => {
    const map = {};
    candidatures.filter(c => c.status === 'confirmed').forEach(c => {
      const key = `${c.company_registration_id}_${c.designer_registration_id}`;
      map[key] = c;
    });
    return map;
  }, [candidatures]);

  const handleCellClick = (slotId, companyId) => {
    const existing = meetingAt[`${slotId}_${companyId}`];
    if (existing) {
      // Show modal with delete option
      setModal({ slotId, companyId, existingMeeting: existing });
    } else {
      // Show modal to assign designer
      setModal({ slotId, companyId, existingMeeting: null });
    }
  };

  const handleAssign = async (designerId) => {
    if (!modal) return;
    const { slotId, companyId } = modal;
    const { error } = await upsertMeeting({
      event_id: eventId,
      slot_id: slotId,
      company_registration_id: companyId,
      designer_registration_id: designerId,
      is_online: slots.find(s => s.id === slotId)?.online || false,
    });
    if (error) {
      alert('Erreur: ' + error.message + '\n(Possible conflit de créneau)');
      return;
    }
    // Keep the shortlist in sync: a planned meeting is a confirmed candidature
    const cand = candidatures.find(c => c.designer_registration_id === designerId && c.company_registration_id === companyId);
    if (cand) {
      if (cand.status !== 'confirmed') await updateCandidatureStatus(cand.id, 'confirmed');
    } else {
      const maxPriority = candidatures.filter(c => c.designer_registration_id === designerId).reduce((m, c) => Math.max(m, c.priority || 0), 0);
      await upsertCandidature({ event_id: eventId, designer_registration_id: designerId, company_registration_id: companyId, priority: maxPriority + 1, status: 'confirmed' });
    }
    setModal(null);
    loadData();
  };

  const handleDelete = async (meeting) => {
    await deleteMeeting(meeting.id);
    // Moving a rendez-vous = new slot created, then old one deleted: keep the candidature confirmed while another slot remains
    const stillPlanned = meetings.some(m => m.id !== meeting.id && m.designer_registration_id === meeting.designer_registration_id && m.company_registration_id === meeting.company_registration_id);
    const cand = candidatures.find(c => c.designer_registration_id === meeting.designer_registration_id && c.company_registration_id === meeting.company_registration_id);
    if (cand && cand.status === 'confirmed' && !stillPlanned) await updateCandidatureStatus(cand.id, 'pending');
    setModal(null);
    loadData();
  };

  // Available designers for a slot (not already busy)
  const availableDesigners = (slotId) => {
    return designers.filter(d => !designerBusy[`${slotId}_${d.id}`]);
  };

  // Confirmed candidatures (both parties visible) that have no slot yet, with the slots where both are free
  const slotShort = (s) => (s.label || s.id).replace(/ - .*$/, '');
  const toPlan = candidatures
    .filter(c => c.status === 'confirmed'
      && companies.some(co => co.id === c.company_registration_id)
      && designers.some(d => d.id === c.designer_registration_id)
      && !meetings.some(m => m.designer_registration_id === c.designer_registration_id && m.company_registration_id === c.company_registration_id))
    .map(c => {
      const companyFree = slots.filter(s => !meetingAt[`${s.id}_${c.company_registration_id}`]);
      return {
        id: c.id,
        company: companies.find(co => co.id === c.company_registration_id)?.company_name || '',
        designer: designers.find(d => d.id === c.designer_registration_id)?.company_name || '',
        companyFree: companyFree.map(slotShort),
        possible: companyFree.filter(s => !designerBusy[`${s.id}_${c.designer_registration_id}`]).map(slotShort),
      };
    })
    .sort((a, b) => a.company.localeCompare(b.company, 'fr', { sensitivity: 'base' }));

  const tableLabel = (company) => {
    const t = company.metadata?.table, e = company.metadata?.etage, n = company.metadata?.table_note;
    return t || e ? `${t ? `Table ${t}` : ''}${t && e ? ' · ' : ''}${e || ''}${n ? ` (${n})` : ''}` : '';
  };

  const exportXlsx = () => {
    const designerName = (id) => { const d = allDesigners.find(x => x.id === id); return d ? (d.company_name || d.user_email) : ''; };
    const designerOf = (id) => allDesigners.find(x => x.id === id);
    const slotTime = (s) => (s.label || s.id).replace(/^RDV \d+ · /, '');
    const sortedDesigners = [...designers].sort((a, b) => (a.company_name || '').localeCompare(b.company_name || '', 'fr', { sensitivity: 'base' }));

    // Sheet 1: grid by company
    const byCompany = [['Entreprise', 'Table', 'Étage', 'Contact', ...slots.map(s => `RDV ${slots.indexOf(s) + 1} · ${slotTime(s)}`)]];
    companies.forEach(c => {
      const rep = c.representative_1 || {};
      byCompany.push([c.company_name, c.metadata?.table || '', c.metadata?.etage || '', `${rep.firstname || ''} ${rep.name || ''}`.trim(),
        ...slots.map(s => designerName(meetingAt[`${s.id}_${c.id}`]?.designer_registration_id))]);
    });
    // Sheet 2: grid by designer
    const byDesigner = [['Designer', 'Contact', 'Email', 'Nb RDV', ...slots.map(s => `RDV ${slots.indexOf(s) + 1} · ${slotTime(s)}`)]];
    sortedDesigners.forEach(d => {
      const mine = meetings.filter(m => m.designer_registration_id === d.id);
      if (!mine.length) return;
      const rep = d.representative_1 || {};
      byDesigner.push([d.company_name, `${rep.firstname || ''} ${rep.name || ''}`.trim(), d.user_email, mine.length,
        ...slots.map(s => { const m = mine.find(x => x.slot_id === s.id); const co = m && companies.find(c => c.id === m.company_registration_id); return co ? `${co.company_name}${co.metadata?.table ? ` (T${co.metadata.table})` : ''}` : ''; })]);
    });
    // Sheet 3: flat list
    const list = [['Créneau', 'Heure', 'Entreprise', 'Table', 'Étage', 'Designer', 'Contact designer', 'Email designer']];
    slots.forEach((s, i) => companies.forEach(c => {
      const m = meetingAt[`${s.id}_${c.id}`]; if (!m) return;
      const d = designerOf(m.designer_registration_id) || {}; const rep = d.representative_1 || {};
      list.push([`RDV ${i + 1}`, slotTime(s), c.company_name, c.metadata?.table || '', c.metadata?.etage || '', d.company_name || '', `${rep.firstname || ''} ${rep.name || ''}`.trim(), d.user_email || '']);
    }));

    const wb = XLSX.utils.book_new();
    const add = (rows, name, widths) => { const ws = XLSX.utils.aoa_to_sheet(rows); ws['!cols'] = widths.map(w => ({ wch: w })); ws['!freeze'] = { xSplit: 1, ySplit: 1 }; XLSX.utils.book_append_sheet(wb, ws, name); };
    add(byCompany, 'Par entreprise', [32, 6, 6, 24, ...slots.map(() => 26)]);
    add(byDesigner, 'Par designer', [32, 24, 30, 7, ...slots.map(() => 28)]);
    add(list, 'Liste', [8, 15, 32, 6, 6, 32, 24, 30]);
    XLSX.writeFile(wb, `SDO2026_planning_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (loading) return <LoadingBox>Chargement du planning...</LoadingBox>;

  return (
    <Page>
      <Header>
        <div>
          <BackBtn onClick={() => navigate(`/admin/events/sde?eid=${eventId}`)}>← Retour au dashboard</BackBtn>
          <h1 style={{ marginTop: '0.75rem' }}>{event?.title_fr || 'SDE'} — Planning</h1>
        </div>
        <ExportBtn onClick={exportXlsx}>Exporter XLSX</ExportBtn>
      </Header>

      <StatsRow>
        <StatPill><span>{meetings.length}</span> rencontres planifiées</StatPill>
        <StatPill><span>{companies.length}</span> entreprises</StatPill>
        <StatPill><span>{designers.length}</span> designers</StatPill>
        <StatPill><span>{slots.length}</span> créneaux</StatPill>
        <StatPill style={toPlan.length ? { borderColor: theme.warning, background: '#FFFBEB' } : undefined}><span>{toPlan.length}</span> confirmées à planifier</StatPill>
      </StatsRow>

      <LegendRow>
        <LegendItem><LegendBox color="#EFF6FF" border="#BFDBFE" /> Rencontre planifiée</LegendItem>
        <LegendItem><LegendBox color="#F0FDF4" border="#BBF7D0" /> Candidature confirmée (suggestion)</LegendItem>
        <LegendItem><LegendBox color="#FEF2F2" border="#FECACA" /> Conflit</LegendItem>
        <LegendItem><LegendBox color="white" border={theme.gray[300]} /> Disponible</LegendItem>
      </LegendRow>

      {toPlan.length > 0 && (
        <details style={{ marginBottom: '1rem', background: '#FFFBEB', border: `1px solid ${theme.warning}`, borderRadius: 8, padding: '0.6rem 1rem', fontSize: '0.85rem' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#92400E' }}>
            {toPlan.length} rendez-vous confirmés dans la shortlist mais pas encore placés au planning
          </summary>
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem', lineHeight: 1.7 }}>
            {toPlan.map(t => (
              <li key={t.id}>
                <strong>{t.company}</strong> × {t.designer} — {t.possible.length
                  ? <>créneaux possibles : {t.possible.join(', ')}</>
                  : <span style={{ color: theme.error }}>aucun créneau commun libre (entreprise libre : {t.companyFree.join(', ') || 'complète'})</span>}
              </li>
            ))}
          </ul>
        </details>
      )}

      <GridWrapper>
        <Grid>
          <thead>
            <tr>
              <SlotHeader style={{ position: 'sticky', left: 0, zIndex: 3, borderRight: `2px solid ${theme.gray[300]}` }}>
                Entreprise
              </SlotHeader>
              {slots.map(s => (
                <SlotHeader key={s.id}>
                  {s.label}
                  {s.online && <span style={{ display: 'block', fontSize: '0.65rem', color: theme.primary }}>en ligne</span>}
                </SlotHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {companies.map(company => (
              <tr key={company.id}>
                <CompanyHeader title={`${company.company_name}${tableLabel(company) ? ` — ${tableLabel(company)}` : ''}`}>
                  {company.company_name || company.user_email}
                  {tableLabel(company) && <TableTag>{tableLabel(company)}</TableTag>}
                </CompanyHeader>
                {slots.map(slot => {
                  const meeting = meetingAt[`${slot.id}_${company.id}`];
                  const designer = meeting ? allDesigners.find(d => d.id === meeting.designer_registration_id) : null;
                  const designerHidden = designer && designer.status !== 'submitted' && designer.status !== 'validated';
                  const isCandidate = !meeting && Object.keys(confirmedCandidatures).some(k =>
                    k.startsWith(`${company.id}_`)
                  );

                  return (
                    <Cell
                      key={slot.id}
                      hasMeeting={!!meeting}
                      isCandidate={isCandidate}
                      onClick={() => handleCellClick(slot.id, company.id)}
                    >
                      {meeting && designer && (
                        <MeetingPill title={designerHidden ? `${designer.company_name} — designer ${designer.status}, à retirer ou revalider` : (designer.company_name || designer.user_email)} style={designerHidden ? { background: '#FEF2F2', color: '#991B1B', textDecoration: 'line-through' } : undefined}>
                          {designer.company_name || designer.user_email?.split('@')[0] || '?'}
                        </MeetingPill>
                      )}
                      {isCandidate && !meeting && <CandidateDot />}
                    </Cell>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Grid>
      </GridWrapper>

      {/* Assignment / Delete Modal */}
      {modal && (
        <ModalOverlay onClick={() => setModal(null)}>
          <Modal onClick={e => e.stopPropagation()}>
            {modal.existingMeeting ? (
              <>
                <h3>Rencontre existante</h3>
                <p style={{ fontSize: '0.9rem', color: theme.gray[600], marginBottom: '0.75rem' }}>
                  {(() => {
                    const d = allDesigners.find(d => d.id === modal.existingMeeting.designer_registration_id);
                    return d ? (d.company_name || d.user_email) : 'Designer';
                  })()} →{' '}
                  {companies.find(c => c.id === modal.companyId)?.company_name || 'Entreprise'}
                </p>
                <ModalRow>
                  <Btn onClick={() => setModal(null)}>Annuler</Btn>
                  <Btn variant="danger" onClick={() => handleDelete(modal.existingMeeting)}>Supprimer</Btn>
                </ModalRow>
              </>
            ) : (
              <>
                <h3>
                  Assigner un designer — {companies.find(c => c.id === modal.companyId)?.company_name || ''}
                </h3>
                <p style={{ fontSize: '0.8rem', color: theme.gray[500], marginBottom: '0.75rem' }}>
                  Créneau: {slots.find(s => s.id === modal.slotId)?.label}
                </p>
                <ModalSelect
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) handleAssign(e.target.value);
                  }}
                >
                  <option value="">— Sélectionner un designer —</option>
                  {availableDesigners(modal.slotId).map(d => {
                    const hasCandidate = confirmedCandidatures[`${modal.companyId}_${d.id}`];
                    return (
                      <option key={d.id} value={d.id}>
                        {d.company_name || d.user_email} {hasCandidate ? '⭐' : ''}
                      </option>
                    );
                  })}
                </ModalSelect>
                <ModalRow>
                  <Btn onClick={() => setModal(null)}>Annuler</Btn>
                </ModalRow>
              </>
            )}
          </Modal>
        </ModalOverlay>
      )}
    </Page>
  );
};

export default SDEPlanningAdmin;
